import { useState, useRef, useCallback, createContext, useContext } from 'react';

// ── ActiveTab Hook ────────────────────────────────────────────────────────────

export interface ActiveTabContextValue {
  /** 目前選取的 tab */
  activeTab: string;
  /** 切換 tab（會觸發 navigation guard 檢查） */
  setActiveTab: (tab: string) => void;
  /** 設定頁面離開防護函式，回傳 false 則阻止切換 */
  setNavigationGuard: (guard: (() => boolean) | null) => void;
}

/**
 * 建立 tab 切換狀態與 navigation guard 邏輯。
 * onTabChange 可選，用來在切換時執行額外副作用（例如重置生成狀態）。
 */
export function useActiveTabState(
  defaultTab = 'toolkit',
  onTabChange?: (from: string, to: string) => void,
): ActiveTabContextValue {
  const [activeTab, setActiveTabState] = useState(defaultTab);
  const navigationGuardRef = useRef<(() => boolean) | null>(null);

  const setNavigationGuard = useCallback((guard: (() => boolean) | null) => {
    navigationGuardRef.current = guard;
  }, []);

  const setActiveTab = useCallback((tab: string) => {
    if (navigationGuardRef.current && !navigationGuardRef.current()) return;
    setActiveTabState(prev => {
      onTabChange?.(prev, tab);
      return tab;
    });
  }, [onTabChange]);

  return { activeTab, setActiveTab, setNavigationGuard };
}

// ── ActiveTab Context（供跨元件使用） ──────────────────────────────────────────

export const ActiveTabContext = createContext<ActiveTabContextValue | null>(null);

export const useActiveTab = (): ActiveTabContextValue => {
  const ctx = useContext(ActiveTabContext);
  if (!ctx) throw new Error('useActiveTab must be used within ActiveTabProvider');
  return ctx;
};
